import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Bed, Bath, Maximize2, MapPin, Calendar, Check, ArrowLeft, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fullPrice, formatPrice } from "../lib/api";
import MortgageCalculator from "../components/MortgageCalculator";
import Seo from "../components/Seo";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => {
      setP(r.data);
      setForm((f) => ({
        ...f,
        message: `I'm interested in ${r.data.title} (${r.data.address}). Please contact me with more details.`,
      }));
    }).catch(() => setP(false));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, property_id: id });
      toast.success("Message sent! An agent will reach out within 1 business hour.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (p === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl">Listing not found.</h1>
        <Link to="/properties" className="text-[#E63946] mt-6 inline-block">
          ← Back to all properties
        </Link>
      </div>
    );
  }
  if (!p) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-gray-500">Loading…</div>;
  }

  return (
    <div className="bg-stone-50 min-h-screen" data-testid={`property-detail-${p.id}`}>
      <Seo
        title={`${p.title} · ${fullPrice(p.price)}`}
        description={`${p.beds} bd · ${p.baths} ba · ${p.sqft.toLocaleString()} sqft in ${p.neighborhood}, ${p.city}. ${p.description.slice(0, 140)}`}
        image={p.images?.[0]}
        path={`/properties/${p.id}`}
        type="product"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8">
        <Link
          to="/properties"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#E63946]"
          data-testid="property-back"
        >
          <ArrowLeft className="w-4 h-4" /> All properties
        </Link>
      </div>

      {/* Gallery */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:h-[34rem]">
          <div className="lg:col-span-8 rounded-xl overflow-hidden bg-gray-100 aspect-[4/3] lg:aspect-auto">
            <img
              src={p.images[activeImg] || p.images[0]}
              alt={p.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 lg:grid-rows-3 gap-3">
            {p.images.slice(1, 4).map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i + 1)}
                className="rounded-xl overflow-hidden bg-gray-100 aspect-[4/3] lg:aspect-auto"
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={src} alt={`${p.title} ${i + 2}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header info */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {p.featured && (
              <span className="bg-[#E63946] text-white text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded">
                Featured
              </span>
            )}
            <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500 bg-white border border-gray-200 px-2.5 py-1 rounded">
              {p.status}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-gray-500">
              {p.property_type}
            </span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-[#1A1A1A]">
            {p.title}
          </h1>
          <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <MapPin className="w-4 h-4 text-[#E63946]" strokeWidth={1.75} />
            {p.address}, {p.city} · {p.neighborhood}
          </div>

          <div className="flex items-baseline gap-4 mt-8 pb-8 border-b border-gray-200">
            <div className="font-display text-5xl font-semibold tracking-tighter text-[#1A1A1A]">
              {fullPrice(p.price)}
            </div>
            <div className="text-sm text-gray-500">
              {formatPrice(Math.round(p.price / p.sqft))}/sqft
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
            <Stat icon={<Bed className="w-5 h-5" />} value={p.beds} label="Bedrooms" />
            <Stat icon={<Bath className="w-5 h-5" />} value={p.baths} label="Bathrooms" />
            <Stat icon={<Maximize2 className="w-5 h-5" />} value={p.sqft.toLocaleString()} label="Sq Feet" />
            <Stat icon={<Calendar className="w-5 h-5" />} value={p.year_built} label="Year Built" />
          </div>

          <div className="mt-12">
            <h2 className="font-display text-2xl font-semibold mb-4">About this home</h2>
            <p className="text-[#444] leading-relaxed">{p.description}</p>
          </div>

          <div className="mt-12">
            <h2 className="font-display text-2xl font-semibold mb-4">Property features</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                  <span className="w-6 h-6 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12">
            <MortgageCalculator price={p.price} />
          </div>
        </div>

        {/* Inquiry form */}
        <aside className="lg:col-span-4">
          <div className="sticky top-28 bg-white border border-gray-200 rounded-2xl p-7">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E63946] mb-2">
              Schedule a Tour
            </div>
            <h3 className="font-display text-2xl font-semibold mb-1">
              Inquire about this home
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              A Gens Realty agent will respond within 1 business hour.
            </p>
            <form onSubmit={submit} className="space-y-3" data-testid="inquiry-form">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E63946]"
                data-testid="inquiry-name"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E63946]"
                data-testid="inquiry-email"
              />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone (optional)"
                className="w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E63946]"
                data-testid="inquiry-phone"
              />
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full bg-stone-50 border border-gray-200 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E63946] resize-none"
                data-testid="inquiry-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#E63946] hover:bg-[#c9313d] text-white font-semibold py-3.5 rounded-md text-sm disabled:opacity-50 transition-colors"
                data-testid="inquiry-submit"
              >
                {submitting ? "Sending…" : "Request a Showing"}
              </button>
            </form>
            <div className="mt-6 pt-6 border-t border-gray-200 space-y-3 text-sm">
              <a href="tel:8327414662" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#E63946]">
                <Phone className="w-4 h-4" strokeWidth={1.75} /> (832) 741-4662
              </a>
              <a href="mailto:PJoseph@gensrealty.com" className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#E63946]">
                <Mail className="w-4 h-4" strokeWidth={1.75} /> PJoseph@gensrealty.com
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="text-[#E63946] mb-2">{icon}</div>
      <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500 mt-1">{label}</div>
    </div>
  );
}
